import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestoreIcon from '@mui/icons-material/Restore';
import DeletedIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BlockIcon from '@mui/icons-material/Block';

interface DeletedApplication {
  id: string;
  appId: string;
  displayName: string;
  deletedDateTime: string;
  publisherDomain?: string;
  signInAudience?: string;
  createdDateTime?: string;
  description?: string;
}

interface RestoreAppFormProps {
  onCancel: () => void;
  onSuccess?: () => void
}

const RestoreAppForm: React.FC<RestoreAppFormProps> = ({ onCancel, onSuccess }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [deletedApps, setDeletedApps] = useState<DeletedApplication[]>([]);
  const [filteredApps, setFilteredApps] = useState<DeletedApplication[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState<DeletedApplication | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // New state
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false); // New state
  const [error, setError] = useState<string | null>(null);
  const [restoreResult, setRestoreResult] = useState<{success: boolean; message: string} | null>(null);
  const [deleteResult, setDeleteResult] = useState<{success: boolean; message: string} | null>(null); // New state


  // Fetch deleted applications on component mount
  useEffect(() => {
    fetchDeletedApplications();
  }, []);

  // Filter applications based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredApps(deletedApps);
    } else {
      const filtered = deletedApps.filter(app =>
        app.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.appId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.publisherDomain?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredApps(filtered);
    }
  }, [searchQuery, deletedApps]);

  const getGraphToken = async (): Promise<string> => {
    const request = {
      scopes: [
        "Application.ReadWrite.All",        // For application CRUD operations
        // "Directory.AccessAsUser.All",       // For directory operations as user
        // "Directory.Read.All"                // For reading directory objects
        "Directory.ReadWrite.All"           // Combined read/write for directory 
      ],
      account: accounts[0],
    };
    
    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(request);
        return response.accessToken;
      }
      throw err;
    }
  };

  const fetchDeletedApplications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token)
      });

      // Fetch deleted applications using directory objects endpoint
      const response = await client
        .api('/directory/deletedItems/microsoft.graph.application')
        .select('id,appId,displayName,deletedDateTime,publisherDomain,signInAudience,createdDateTime,description')
        .get();

      const apps = response.value || [];
      
      // Sort client-side by deletion date (newest first)
      const sortedApps = apps.sort((a: DeletedApplication, b: DeletedApplication) => {
        return new Date(b.deletedDateTime).getTime() - new Date(a.deletedDateTime).getTime();
      });
      
      setDeletedApps(sortedApps);
      setFilteredApps(sortedApps);
    } catch (err: any) {
      console.error('Error fetching deleted applications:', err);
      setError(
        err.code === 'Forbidden' 
          ? 'You do not have permission to view deleted applications. The Application.ReadWrite.All permission is required.'
          : `Failed to fetch deleted applications: ${err.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if app can be restored
  const canBeRestored = (deletedDate: string): boolean => {
    const deleted = new Date(deletedDate);
    const now = new Date();
    const daysDiff = (now.getTime() - deleted.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 30;
  };

  const handleRestoreClick = (app: DeletedApplication) => {
    setSelectedApp(app);
    setRestoreDialogOpen(true);
    setRestoreResult(null);
  };

  // New function to handle permanent deletion
  const handlePermanentDeleteClick = (app: DeletedApplication) => {
    setSelectedApp(app);
    setDeleteDialogOpen(true);
    setDeleteResult(null);
  };

  // New function to confirm permanent deletion
  const handlePermanentDeleteConfirm = async () => {
    if (!selectedApp) return;

    setDeleting(true);
    setDeleteResult(null);

    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token)
      });

      // Permanently delete the application
      await client
        .api(`/directory/deletedItems/${selectedApp.id}`)
        .delete();

      setDeleteResult({
        success: true,
        message: `Application "${selectedApp.displayName}" has been permanently deleted and cannot be recovered.`
      });

      // Remove the deleted app from the list
      setDeletedApps(prev => prev.filter(app => app.id !== selectedApp.id));
      
      // Close dialog after a short delay
      setTimeout(() => {
        setDeleteDialogOpen(false);
        setSelectedApp(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error permanently deleting application:', err);
      setDeleteResult({
        success: false,
        message: `Failed to permanently delete application: ${err.message || 'Unknown error'}`
      });
    } finally {
      setDeleting(false);
    }
  };  

  const handleRestoreConfirm = async () => {
    if (!selectedApp) return;

    setRestoring(true);
    setRestoreResult(null);

    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token)
      });

      let restorationDetails = [];
      let warnings = [];

      // Step 1: Restore the application (matches portal)
      console.log('Restoring application...');
      const restoredApp = await client
        .api(`/directory/deletedItems/${selectedApp.id}/restore`)
        .post({});

      restorationDetails.push('✅ Application registration restored successfully');
      console.log('Application restored:', restoredApp);

      // Step 2: Attempt to restore service principal (matches portal behavior)
      console.log('Checking for deleted service principal...');
      
      try {
        // First check if SP already exists (shouldn't happen but let's be safe)
        const existingSPs = await client
          .api('/servicePrincipals')
          .filter(`appId eq '${selectedApp.appId}'`)
          .select('id,appId,displayName,createdDateTime')
          .get();

        if (existingSPs.value && existingSPs.value.length > 0) {
          restorationDetails.push('✅ Service principal already exists');
          console.log('Service principal already exists:', existingSPs.value[0].id);
        } else {
          // Try to find and restore deleted service principal (like portal does)
          console.log('Looking for deleted service principal...');
          
          try {
            // This requires Directory.ReadWrite.All permission
            const deletedSPs = await client
              .api('/directory/deletedItems/microsoft.graph.servicePrincipal')
              .filter(`appId eq '${selectedApp.appId}'`)
              .select('id,appId,displayName,deletedDateTime')
              .get();

            if (deletedSPs.value && deletedSPs.value.length > 0) {
              // Restore the original service principal (like portal)
              const spToRestore = deletedSPs.value[0];
              console.log('Restoring original service principal:', spToRestore.id);
              
              await client
                .api(`/directory/deletedItems/${spToRestore.id}/restore`)
                .post({});
              
              restorationDetails.push('✅ Original service principal restored successfully');
              console.log('Service principal restored successfully');
            } else {
              // Service principal not found in deleted items, create new one
              console.log('Deleted service principal not found, creating new one...');
              
              const newSP = await client
                .api('/servicePrincipals')
                .post({
                  appId: selectedApp.appId,
                  displayName: selectedApp.displayName
                });
              
              restorationDetails.push('🆕 New service principal created (original not found)');
              warnings.push('⚠️ Original service principal could not be restored - created new one');
              warnings.push('⚠️ May need to reconfigure: role assignments, group memberships, SSO settings');
              console.log('New service principal created:', newSP.id);
            }
          } catch (spSearchError: any) {
            console.warn('Could not search deleted service principals (permission issue):', spSearchError);
            
            // Fallback: create new service principal
            try {
              console.log('Fallback: Creating new service principal...');
              
              const newSP = await client
                .api('/servicePrincipals')
                .post({
                  appId: selectedApp.appId,
                  displayName: selectedApp.displayName
                });
              
              restorationDetails.push('🆕 New service principal created (fallback)');
              warnings.push('⚠️ Could not access deleted service principals - created new one instead');
              warnings.push('⚠️ This may require additional permissions (Directory.ReadWrite.All) for full restoration');
              console.log('Fallback service principal created:', newSP.id);
            } catch (createError: any) {
              restorationDetails.push('❌ Service principal creation failed');
              warnings.push('⚠️ Manual service principal creation required in Azure portal');
              console.error('Failed to create service principal:', createError);
            }
          }
        }
      } catch (spError: any) {
        console.error('Service principal operation failed:', spError);
        restorationDetails.push('❌ Service principal restoration failed');
        warnings.push('⚠️ Manual service principal restoration may be required');
      }

      // Combine results and warnings
      const allMessages = [...restorationDetails];
      if (warnings.length > 0) {
        allMessages.push(''); // Empty line
        allMessages.push('⚠️ RESTORATION NOTES:');
        allMessages.push(...warnings);
        allMessages.push(''); // Empty line
        allMessages.push('🔍 VERIFICATION STEPS:');
        allMessages.push('• Check Azure portal > Enterprise applications');
        allMessages.push('• Verify application permissions and assignments');
        allMessages.push('• Test application functionality');
      }

      const detailsMessage = allMessages.join('\n');
      const hasWarnings = warnings.length > 0;
      const isFullSuccess = !hasWarnings;
      
      setRestoreResult({
        success: true,
        message: `Application "${selectedApp.displayName}" restoration ${isFullSuccess ? 'completed' : 'partially completed'}:\n\n${detailsMessage}\n\n${
          isFullSuccess 
            ? '✅ Full restoration successful!' 
            : '⚠️ Partial restoration - please review notes above and verify in Azure portal.'
        }`
      });

      if (onSuccess) {
        onSuccess();
      }

      // Remove the restored app from the list
      setDeletedApps(prev => prev.filter(app => app.id !== selectedApp.id));
      
      // Longer delay if there are warnings
      setTimeout(() => {
        setRestoreDialogOpen(false);
        setSelectedApp(null);
      }, hasWarnings ? 8000 : 5000);

    } catch (err: any) {
      console.error('Error restoring application:', err);
      setRestoreResult({
        success: false,
        message: `Failed to restore application: ${err.message || 'Unknown error'}\n\nThe restoration may be incomplete. Please check the Azure portal and complete manually if needed.`
      });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getDaysUntilPermanentDeletion = (deletedDate: string) => {
    const deleted = new Date(deletedDate);
    const now = new Date();
    const thirtyDaysLater = new Date(deleted.getTime() + (30 * 24 * 60 * 60 * 1000));
    const daysLeft = Math.ceil((thirtyDaysLater.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    return Math.max(0, daysLeft);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading deleted applications...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#fff8e1', // soft warning background (matches restore theme)
            borderBottom: '1px solid rgba(255, 152, 0, 0.25)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderColor: 'primary.main',
                color: 'primary.main'
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid #ff9800',
              pl: 2,
              py: 0.5
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#e65100',
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <RestoreIcon sx={{ mr: 1 }} />
              Restore Deleted Applications
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Recover an app registration and its service principal from Deleted Items
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Deleted applications are automatically purged after 30 days. 
          Only applications deleted within the last 30 days can be restored.
        </Typography>
      </Alert>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search deleted applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Results Summary */}
      {!error && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredApps.length === 0 
              ? 'No deleted applications found'
              : `Found ${filteredApps.length} deleted application${filteredApps.length !== 1 ? 's' : ''}`
            }
            {searchQuery && ` matching "${searchQuery}"`}
          </Typography>
        </Box>
      )}

      {/* Deleted Applications Table */}
      {!error && filteredApps.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Application Name</TableCell>
                <TableCell>App ID</TableCell>
                <TableCell>Deleted Date</TableCell>
                <TableCell>Days Left</TableCell>
                <TableCell>Publisher Domain</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApps.map((app) => {
                const daysLeft = getDaysUntilPermanentDeletion(app.deletedDateTime);
                const isUrgent = daysLeft <= 7;
                const canRestore = canBeRestored(app.deletedDateTime);
                const isExpired = daysLeft <= 0;
                
                return (
                  <TableRow 
                    key={app.id}
                    sx={{ 
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      ...(isUrgent && { backgroundColor: '#fff3e0' }),
                      ...(isExpired && { backgroundColor: '#fafafa', opacity: 0.8 })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DeletedIcon sx={{ 
                          mr: 1, 
                          color: isExpired ? 'text.disabled' : 'text.secondary', 
                          fontSize: '1.2rem' 
                        }} />
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: isExpired ? 'text.disabled' : 'text.primary'
                            }}
                          >
                            {app.displayName || 'Unknown Application'}
                            {isExpired && (
                              <Chip 
                                label="EXPIRED" 
                                size="small" 
                                color="error" 
                                variant="outlined"
                                sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                          {app.description && (
                            <Typography variant="caption" color="text.secondary">
                              {app.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.8rem',
                          color: isExpired ? 'text.disabled' : 'text.primary'
                        }}
                      >
                        {app.appId}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon sx={{ 
                          mr: 0.5, 
                          fontSize: '1rem', 
                          color: isExpired ? 'text.disabled' : 'text.secondary' 
                        }} />
                        <Typography 
                          variant="body2"
                          sx={{ color: isExpired ? 'text.disabled' : 'text.primary' }}
                        >
                          {formatDate(app.deletedDateTime)}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={isExpired ? 'EXPIRED' : `${daysLeft} days`}
                        size="small"
                        color={isExpired ? 'error' : isUrgent ? 'error' : daysLeft <= 14 ? 'warning' : 'default'}
                        variant={isExpired || isUrgent ? 'filled' : 'outlined'}
                        icon={isExpired ? <BlockIcon /> : isUrgent ? <WarningIcon /> : undefined}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography 
                        variant="body2"
                        sx={{ color: isExpired ? 'text.disabled' : 'text.primary' }}
                      >
                        {app.publisherDomain || 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {/* Restore Button */}
                        <Tooltip title={canRestore ? "Restore Application" : "Cannot restore - application expired after 30 days"}>
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={canRestore ? <RestoreIcon /> : <BlockIcon />}
                              onClick={() => handleRestoreClick(app)}
                              disabled={!canRestore}
                              sx={{
                                backgroundColor: canRestore ? '#ff9800' : '#e0e0e0',
                                color: canRestore ? '#fff' : '#9e9e9e',
                                '&:hover': { 
                                  backgroundColor: canRestore ? '#f57c00' : '#e0e0e0' 
                                },
                                '&:disabled': {
                                  backgroundColor: '#e0e0e0',
                                  color: '#9e9e9e'
                                },
                                borderRadius: 2,
                                textTransform: 'none',
                                minWidth: '90px'
                              }}
                            >
                              {canRestore ? 'Restore' : 'Expired'}
                            </Button>
                          </span>
                        </Tooltip>

                        {/* Permanent Delete Button */}
                        <Tooltip title="Permanently delete application (cannot be undone)">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => handlePermanentDeleteClick(app)}
                            color="error"
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              minWidth: '90px',
                              '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty State */}
      {!error && filteredApps.length === 0 && !loading && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <RestoreIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery ? 'No matching applications found' : 'No deleted applications found'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery 
                ? 'Try adjusting your search terms or clear the search to see all deleted applications.'
                : 'There are no deleted applications available for restoration at this time.'
              }
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog 
        open={restoreDialogOpen} 
        onClose={() => !restoring && setRestoreDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <RestoreIcon sx={{ mr: 1, color: '#ff9800' }} />
          Restore Application
        </DialogTitle>
        
        <DialogContent>
          {restoreResult ? (
            <Alert 
              severity={restoreResult.success ? 'success' : 'error'}
              icon={restoreResult.success ? <CheckCircleIcon /> : <WarningIcon />}
              sx={{ mb: 2 }}
            >
              {restoreResult.message}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Restore Application Registration</strong><br />
                  This will attempt to fully restore both the application registration and its service principal. The restoration includes:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>Application object with all settings and permissions</li>
                  <li>Original service principal with role assignments (if possible)</li>
                  <li>Enterprise application functionality</li>
                </Box>
              </Alert>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Permission Requirements:</strong><br />
                  • Application.ReadWrite.All - For application operations<br />
                  • Directory.ReadWrite.All - For reading and writing directory objects<br />
                  <br />
                  These permissions allow restoration of both app registration and service principal using your user credentials.
                </Typography>
              </Alert>

              {selectedApp && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Application Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Display Name" 
                              secondary={selectedApp.displayName || 'Unknown Application'}
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Application ID" 
                              secondary={
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {selectedApp.appId}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Publisher Domain" 
                              secondary={selectedApp.publisherDomain || 'N/A'}
                            />
                          </ListItem>
                        </List>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Deleted On" 
                              secondary={formatDate(selectedApp.deletedDateTime)}
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Days Until Permanent Deletion" 
                              secondary={
                                <Chip 
                                  label={`${getDaysUntilPermanentDeletion(selectedApp.deletedDateTime)} days`}
                                  size="small"
                                  color={getDaysUntilPermanentDeletion(selectedApp.deletedDateTime) <= 7 ? 'error' : 'default'}
                                />
                              }
                            />
                          </ListItem>
                          {selectedApp.signInAudience && (
                            <ListItem disablePadding>
                              <ListItemText 
                                primary="Supported Account Types" 
                                secondary={selectedApp.signInAudience}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          {!restoreResult && (
            <>
              <Button 
                onClick={() => setRestoreDialogOpen(false)}
                disabled={restoring}
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestoreConfirm}
                disabled={restoring}
                variant="contained"
                startIcon={restoring ? <CircularProgress size={16} /> : <RestoreIcon />}
                sx={{
                  backgroundColor: '#ff9800',
                  '&:hover': { backgroundColor: '#f57c00' }
                }}
              >
                {restoring ? 'Restoring...' : 'Restore Application'}
              </Button>
            </>
          )}
          
          {restoreResult && (
            <Button 
              onClick={() => setRestoreDialogOpen(false)}
              variant="contained"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

     {/* Permanent Delete Confirmation Dialog - NEW */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <DeleteForeverIcon sx={{ mr: 1, color: '#d32f2f' }} />
          Permanently Delete Application
        </DialogTitle>
        
        <DialogContent>
          {deleteResult ? (
            <Alert 
              severity={deleteResult.success ? 'success' : 'error'}
              icon={deleteResult.success ? <CheckCircleIcon /> : <WarningIcon />}
              sx={{ mb: 2 }}
            >
              {deleteResult.message}
            </Alert>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>⚠️ CRITICAL WARNING: This action cannot be undone!</strong><br />
                  Permanently deleting this application will immediately remove it from Azure AD forever. 
                  The application cannot be restored after this operation.
                </Typography>
              </Alert>

              {selectedApp && (
                <Card variant="outlined" sx={{ mb: 2, borderColor: '#d32f2f' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="error">
                      Application to be PERMANENTLY DELETED
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Display Name" 
                              secondary={selectedApp.displayName || 'Unknown Application'}
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Application ID" 
                              secondary={
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {selectedApp.appId}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Publisher Domain" 
                              secondary={selectedApp.publisherDomain || 'N/A'}
                            />
                          </ListItem>
                        </List>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Originally Deleted On" 
                              secondary={formatDate(selectedApp.deletedDateTime)}
                            />
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemText 
                              primary="Current Status" 
                              secondary={
                                <Chip 
                                  label={canBeRestored(selectedApp.deletedDateTime) ? 'RESTORABLE' : 'EXPIRED'}
                                  size="small"
                                  color={canBeRestored(selectedApp.deletedDateTime) ? 'warning' : 'error'}
                                />
                              }
                            />
                          </ListItem>
                          {selectedApp.signInAudience && (
                            <ListItem disablePadding>
                              <ListItemText 
                                primary="Supported Account Types" 
                                secondary={selectedApp.signInAudience}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Why permanently delete?</strong><br />
                  • Free up application quota in your tenant<br />
                  • Remove applications that will never be restored<br />
                  • Clean up expired applications that cannot be restored anyway
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          {!deleteResult && (
            <>
              <Button 
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleting}
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePermanentDeleteConfirm}
                disabled={deleting}
                variant="contained"
                color="error"
                startIcon={deleting ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
                sx={{
                  '&:hover': { backgroundColor: '#b71c1c' }
                }}
              >
                {deleting ? 'Deleting Forever...' : 'DELETE PERMANENTLY'}
              </Button>
            </>
          )}
          
          {deleteResult && (
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              variant="contained"
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RestoreAppForm;